/**
 * LoadingState primitive (Plan 06-01, Task 1).
 *
 * Warm-skeleton shimmer placeholder used while a route or tile is fetching
 * data. Designed to communicate layout shape (lines of varying widths)
 * without resorting to a clinical spinner — see 06-UI-SPEC.md D-03.
 *
 * Renders N (default 3, max 6) skeleton bars with widths that mimic
 * natural text-line variation. Each bar uses the `bg-skeleton-base`
 * Phase 6 alias token; the gradient shimmer is applied via the
 * `motion-safe:animate-skeleton-shimmer` utility declared in
 * theme.css (06-01 Task 3). Under `prefers-reduced-motion: reduce`
 * the motion-safe variant detaches and the bar stays static — required
 * for D-11 (reduced-motion contract).
 *
 * ARIA:
 *   - root `role="status"` so screen readers track the busy state.
 *   - visually-hidden `<span className="sr-only" aria-live="polite">`
 *     resolves to "Laster…" via `common.loading` (06-01 Task 4).
 */
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

export interface LoadingStateProps {
  /** Number of skeleton lines (default 3). */
  lines?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
}

const WIDTH_BY_INDEX: readonly string[] = [
  'w-full',
  'w-17/20',
  'w-3/5',
  'w-4/5',
  'w-7/10',
  'w-19/20',
];

export function LoadingState({
  lines = 3,
  className,
}: LoadingStateProps = {}): JSX.Element {
  const { t } = useTranslation();
  const loadingLabel = t('common.loading');

  const bars = Array.from({ length: lines }, (_unused, i) => {
    const width = WIDTH_BY_INDEX[i] ?? 'w-4/5';
    return (
      <div
        key={i}
        data-testid="skeleton-bar"
        className={[
          'h-4 rounded-pill bg-skeleton-base motion-safe:animate-skeleton-shimmer',
          width,
        ].join(' ')}
      />
    );
  });

  return (
    <div
      role="status"
      data-testid="loading-state"
      className={['flex flex-col gap-2 p-4', className].filter(Boolean).join(' ')}
    >
      {bars}
      <span className="sr-only" aria-live="polite">
        {loadingLabel}
      </span>
    </div>
  );
}
